import DossierInterface from "../interfaces/DossierInterface";
import { PatchStepsEnum } from "../constants/PatchStepsEnum";

interface FormatCreateAndUpdateDataResult {
    data: {
        data: object;
        method: string;
    }[];
}

type PatchStep = FormatCreateAndUpdateDataResult["data"][number];

const appointmentTypeValue = (
    appointmentType: DossierInterface["appointment_type"],
): string | undefined => {
    if (typeof appointmentType === "object") {
        return appointmentType?.code;
    }
    return appointmentType;
};

const vehiclePatches = (dossier: DossierInterface): PatchStep[] => {
    const patches: PatchStep[] = [];

    if (dossier.person?.email) {
        patches.push({
            data: { email: dossier.person?.email },
            method: PatchStepsEnum.PatchEmail,
        });
    }
    if (dossier.car_license_plate) {
        patches.push({
            data: { car_license_plate: dossier.car_license_plate },
            method: PatchStepsEnum.patchCarLicensePlate,
        });
    }
    if (
        !dossier.car_license_plate &&
        !!dossier.car_brand &&
        !!dossier.car_model
    ) {
        patches.push({
            data: {
                car_brand: dossier.car_brand,
                car_model: dossier.car_model,
            },
            method: PatchStepsEnum.PatchModel,
        });
    }
    if (dossier.car_build_year) {
        patches.push({
            data: { car_build_year: dossier.car_build_year },
            method: PatchStepsEnum.PatchYear,
        });
    }
    if (dossier.appointment_type) {
        patches.push({
            data: {
                appointment_type: appointmentTypeValue(
                    dossier.appointment_type,
                ),
            },
            method: PatchStepsEnum.PatchAppointmentType,
        });
    }

    return patches;
};

const glassDamagePatches = (dossier: DossierInterface): PatchStep[] => {
    const patches: PatchStep[] = [];

    if (dossier.damage_cause?.id) {
        patches.push({
            data: { damage_cause_id: dossier.damage_cause?.id },
            method: PatchStepsEnum.PatchDamageCause,
        });
    }
    if (dossier?.glass_damage?.glass_damage_location) {
        patches.push({
            data: {
                glass_damage_location:
                    dossier?.glass_damage?.glass_damage_location,
            },
            method: PatchStepsEnum.PatchGlassDamageLocation,
        });
    }
    if (dossier?.glass_damage?.glass_damage_size) {
        patches.push({
            data: {
                glass_damage_size: dossier?.glass_damage?.glass_damage_size,
            },
            method: PatchStepsEnum.PatchGlassDamageSize,
        });
    }
    if (dossier?.glass_damage?.glass_damage_exact_location) {
        patches.push({
            data: {
                glass_damage_exact_location:
                    dossier?.glass_damage?.glass_damage_exact_location,
            },
            method: PatchStepsEnum.PatchGlassDamageExactLocation,
        });
    }
    if (dossier?.glass_damage?.glass_damage_exact_size) {
        patches.push({
            data: {
                glass_damage_exact_size:
                    dossier?.glass_damage?.glass_damage_exact_size,
            },
            method: PatchStepsEnum.PatchGlassDamageExactSize,
        });
    }
    if (dossier?.glass_damage?.glass_damage_cause) {
        patches.push({
            data: {
                glass_damage_cause: dossier?.glass_damage?.glass_damage_cause,
            },
            method: PatchStepsEnum.PatchGlassDamageCause,
        });
    }

    return patches;
};

const leaseAndContractorPatches = (dossier: DossierInterface): PatchStep[] => {
    const patches: PatchStep[] = [];

    if (dossier.lease_car !== undefined && dossier.lease_car !== null) {
        patches.push({
            data: { lease_car: dossier.lease_car },
            method: PatchStepsEnum.PatchLeaseCar,
        });
    }
    if (dossier.contractor_id) {
        patches.push({
            data: { contractor_id: dossier.contractor_id },
            method: PatchStepsEnum.PatchContractor,
        });
    }
    if (dossier.lease_type_code) {
        patches.push({
            data: { lease_type_code: dossier.lease_type_code },
            method: PatchStepsEnum.PatchLeaseTypeCode,
        });
    }

    return patches;
};

const appointmentAndPersonPatches = (
    dossier: DossierInterface,
): PatchStep[] => {
    const patches: PatchStep[] = [];

    if (
        !!dossier.establishment_id &&
        !!dossier.date &&
        !!dossier.start_time &&
        !!dossier.end_time
    ) {
        patches.push({
            data: {
                establishment_id: dossier.establishment_id,
                date: dossier.date,
                start_time: dossier.start_time,
                end_time: dossier.end_time,
            },
            method: PatchStepsEnum.PatchAppointment,
        });
    }
    if (dossier.loan_vehicle_code) {
        patches.push({
            data: { loan_vehicle_code: dossier.loan_vehicle_code },
            method: PatchStepsEnum.PatchLoanVehicle,
        });
    }
    if (dossier.damage_date) {
        patches.push({
            data: { damage_date: dossier.damage_date },
            method: PatchStepsEnum.PatchDamageDate,
        });
    }
    if (dossier.damage_number) {
        patches.push({
            data: { damage_number: dossier.damage_number },
            method: PatchStepsEnum.PatchDamageNumber,
        });
    }
    if (
        !!dossier.person?.first_name &&
        !!dossier.person?.email &&
        !!dossier.person?.mobile_number &&
        !!dossier.person?.address
    ) {
        patches.push({
            data: {
                person: {
                    first_name: dossier.person?.first_name,
                    insertion: dossier.person?.insertion,
                    last_name: dossier.person?.last_name,
                    email: dossier.person?.email,
                    mobile_number: dossier.person?.mobile_number,
                    address: {
                        zip_code: dossier.person?.address?.zip_code,
                        house_number: dossier.person?.address?.house_number,
                        house_number_addition:
                            dossier.person?.address?.house_number_addition,
                        street: dossier.person?.address?.street,
                        city: dossier.person?.address?.city,
                        country: dossier.person?.address?.country,
                    },
                },
            },
            method: PatchStepsEnum.PatchPerson,
        });
    }
    if (dossier.green_card_code) {
        patches.push({
            data: {
                green_card_code: dossier.green_card_code,
            },
            method: PatchStepsEnum.PatchGreenCardCode,
        });
    }
    if (dossier.bovag_terms_type) {
        patches.push({
            data: {
                bovag_terms_type: dossier.bovag_terms_type,
            },
            method: PatchStepsEnum.PatchBovagTermsType,
        });
    }

    return patches;
};

export const formatCreateAndUpdateData = (
    dossier: DossierInterface,
): FormatCreateAndUpdateDataResult => {
    return {
        data: [
            ...vehiclePatches(dossier),
            ...glassDamagePatches(dossier),
            ...leaseAndContractorPatches(dossier),
            ...appointmentAndPersonPatches(dossier),
        ],
    };
};
